import { useCallback, useEffect, useReducer, useRef } from "react";
import type { AgentMessage } from "../ai-chat-message-utils";

interface JumpToLatestState {
  isVisible: boolean;
}

type JumpToLatestAction = { type: "show" } | { type: "hide" };

const jumpToLatestInitialState: JumpToLatestState = {
  isVisible: false,
};

const jumpToLatestReducer = (
  state: JumpToLatestState,
  action: JumpToLatestAction,
): JumpToLatestState => {
  const isVisible = action.type === "show";
  return state.isVisible === isVisible ? state : { isVisible };
};

export const useJumpToLatest = (visibleMessages: AgentMessage[], isRunning: boolean) => {
  const [state, dispatch] = useReducer(jumpToLatestReducer, jumpToLatestInitialState);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const latestExchangeAnchorRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(false);

  const updateJumpToLatestVisibility = useCallback(() => {
    const container = messagesContainerRef.current;
    const anchor = latestExchangeAnchorRef.current;
    if (!container || !anchor) {
      dispatch({ type: "hide" });
      return;
    }

    const hasScrolledPastLatestExchange =
      anchor.getBoundingClientRect().top > container.getBoundingClientRect().bottom;

    dispatch({ type: hasScrolledPastLatestExchange ? "show" : "hide" });
  }, []);

  const handleJumpToLatest = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    dispatch({ type: "hide" });
  }, []);

  useEffect(() => {
    isVisibleRef.current = state.isVisible;
  }, [state.isVisible]);

  useEffect(() => {
    if (isVisibleRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleMessages.length, isRunning]);

  useEffect(() => {
    updateJumpToLatestVisibility();
  }, [visibleMessages, updateJumpToLatestVisibility]);

  return {
    isJumpToLatestVisible: state.isVisible,
    messagesContainerRef,
    latestExchangeAnchorRef,
    messagesEndRef,
    updateJumpToLatestVisibility,
    handleJumpToLatest,
  };
};
